import type { Job } from '@/domains/jobs/components/JobCard';
import { addDays, format } from 'date-fns';
import { useMemo } from 'react';
import { calculateTeamCapacity, detectSchedulingConflicts } from '../../utils/capacityCalculator';
import type { CalendarTeam } from '../CalendarView';
import { GanttHeader } from './GanttHeader';
import { GanttResourceRow } from './GanttResourceRow';

interface GanttViewProps {
  jobs: Job[];
  teams: CalendarTeam[];
  onUpdateJob: (jobId: string, updatedJob: Partial<Job>) => void;
  onJobClick?: (jobId: string) => void;
  onDrop: (job: Job, teamId: string, date: string) => void;
  zoomLevel: 'hour' | 'day' | 'week' | 'month';
  daysToShow: number;
  rangeStart: Date;
  viewportWidth?: number; // Available width for day columns
}

export function GanttView({
  jobs,
  teams,
  onUpdateJob,
  onJobClick,
  onDrop,
  zoomLevel,
  daysToShow,
  rangeStart,
  viewportWidth,
}: GanttViewProps) {
  // Calculate capacity client-side for each team
  const teamCapacities = useMemo(() => {
    const capacityByTeam = new Map<string, ReturnType<typeof calculateTeamCapacity>>();

    teams.forEach((team) => {
      const teamJobs = jobs.filter((job) => job.teamId === team.id && job.scheduledDate);
      const hoursPerDay = (team.memberCount || 1) * 8; // 8 hours per team member
      const capacity = calculateTeamCapacity(teamJobs, rangeStart, daysToShow, hoursPerDay);
      capacityByTeam.set(team.id, capacity);
    });

    return capacityByTeam;
  }, [jobs, teams, rangeStart, daysToShow]);

  // Detect scheduling conflicts across all teams
  const conflicts = useMemo(() => {
    const conflictsByTeam = new Map<string, ReturnType<typeof detectSchedulingConflicts>>();

    teams.forEach((team) => {
      const teamJobs = jobs.filter((job) => job.teamId === team.id && job.scheduledDate);
      const teamConflicts = detectSchedulingConflicts(teamJobs);
      if (teamConflicts.size > 0) {
        conflictsByTeam.set(team.id, teamConflicts);
      }
    });

    return conflictsByTeam;
  }, [jobs, teams]);

  // Calculate pixels per day based on zoom level and available viewport width
  const pixelsPerDay = useMemo(() => {
    // If viewportWidth is provided, calculate to fill the viewport
    if (viewportWidth && daysToShow > 0) {
      const SIDEBAR_WIDTH = 200; // Team name column width
      const availableWidth = viewportWidth - SIDEBAR_WIDTH;
      const calculatedPixelsPerDay = Math.floor(availableWidth / daysToShow);

      // Set minimum width based on zoom level to prevent columns from being too narrow
      const minWidthByZoom = {
        hour: 60 * 24, // 60px per hour × 24 hours = 1440px per day
        day: 80, // Minimum for day view
        week: 60, // Minimum for week view
        month: 40, // Minimum for month view
      };

      return Math.max(calculatedPixelsPerDay, minWidthByZoom[zoomLevel] || 80);
    }

    // Fallback to fixed widths if no viewport width provided
    switch (zoomLevel) {
      case 'hour':
        return 60 * 24; // 60px per hour × 24 hours = 1440px per day
      case 'day':
        return 120; // Wide columns for detailed day view
      case 'week':
        return 80; // Medium columns for week view
      case 'month':
        return 40; // Narrow columns for month overview
      default:
        return 80;
    }
  }, [zoomLevel, viewportWidth, daysToShow]);

  // Generate array of dates to display
  const dates = useMemo(() => {
    return Array.from({ length: daysToShow }, (_, i) => addDays(rangeStart, i));
  }, [rangeStart, daysToShow]);

  // Group jobs by team
  const jobsByTeam = useMemo(() => {
    const map = new Map<string, Job[]>();

    // Initialize all teams
    teams.forEach((team) => {
      map.set(team.id, []);
    });

    // Assign scheduled jobs to teams
    jobs.forEach((job) => {
      if (job.scheduledDate && job.teamId) {
        const teamJobs = map.get(job.teamId) || [];
        teamJobs.push(job);
        map.set(job.teamId, teamJobs);
      }
    });

    return map;
  }, [jobs, teams]);

  // Handle drop from sidebar or move between resources
  const handleDrop = (job: Job, resourceId: string, date: Date) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    // Pass to parent which will show time picker modal
    onDrop(job, resourceId, dateStr);
  };

  return (
    <div className="flex-1 flex flex-col overflow-hidden bg-background">
      {/* Fixed Header */}
      <div className="flex-shrink-0">
        <GanttHeader dates={dates} pixelsPerDay={pixelsPerDay} zoomLevel={zoomLevel} />
      </div>

      {/* Scrollable Resource rows */}
      <div className="flex-1 overflow-auto">
        <div className="min-w-max">
          {/* Resource rows */}
          {teams.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <div className="text-sm font-medium mb-2">No teams available</div>
              <div className="text-xs">
                Select teams from the filters above to view the Gantt chart
              </div>
            </div>
          ) : (
            teams.map((team) => {
              const teamJobs = jobsByTeam.get(team.id) || [];
              const teamCapacity = teamCapacities.get(team.id);
              const teamConflicts = conflicts.get(team.id);

              return (
                <GanttResourceRow
                  key={team.id}
                  resourceId={team.id}
                  resourceName={team.name}
                  jobs={teamJobs}
                  rangeStart={rangeStart}
                  pixelsPerDay={pixelsPerDay}
                  totalDays={daysToShow}
                  onDrop={handleDrop}
                  onJobClick={onJobClick}
                  dailyCapacity={teamCapacity}
                  conflicts={teamConflicts}
                  zoomLevel={zoomLevel}
                />
              );
            })
          )}
        </div>
      </div>
    </div>
  );
}
